import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, Eye, Trash2, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Toaster, toast } from "@/components/ui/sonner";
import PageHeader from "@/components/PageHeader";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PreventiviArchivioPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const fetchItems = async (search = "") => {
    setLoading(true);
    try {
      const params = search ? { patient_name: search } : {};
      const { data } = await axios.get(`${API}/api/preventivi`, {
        params,
        withCredentials: true,
      });
      setItems(data.preventivi || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const doSearch = (e) => {
    e.preventDefault();
    fetchItems(q.trim());
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Eliminare questo preventivo?")) return;
    try {
      await axios.delete(`${API}/api/preventivi/${id}`, { withCredentials: true });
      toast.success("Preventivo eliminato");
      if (selected?.id === id) setSelected(null);
      fetchItems(q.trim());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f7fa] bg-grid-light pt-[calc(7px+3.5rem+13px)]" data-testid="preventivi-archivio-page">
      <Toaster position="top-right" theme="light" />
      <div className="max-w-7xl mx-auto px-4 lg:px-6 pb-8">
        <PageHeader
          title="Archivio Preventivi"
          subtitle="Preventivi generati con l'assistente AI"
        />

        <form onSubmit={doSearch} className="flex gap-2 mt-6" data-testid="preventivi-search-form">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca per nome paziente…"
              className="pl-9"
              data-testid="preventivi-search-input"
            />
          </div>
          <Button type="submit" variant="outline" data-testid="preventivi-search-btn">Cerca</Button>
        </form>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mt-6">
          {/* Lista (sinistra 2/5) */}
          <Card className="lg:col-span-2 border-slate-200 shadow-sm">
            <CardContent className="p-0 max-h-[75vh] overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center p-8 text-slate-400 text-sm">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento…
                </div>
              ) : items.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-400">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  Nessun preventivo trovato
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {items.map((p) => (
                    <li key={p.id}>
                      <button
                        onClick={() => setSelected(p)}
                        className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors ${selected?.id === p.id ? "bg-violet-50 border-l-4 border-violet-500" : ""}`}
                        data-testid={`preventivo-item-${p.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-800 truncate">{p.patient_name || "—"}</p>
                            <p className="text-xs text-slate-500 truncate">{p.insurance || "—"} · {p.reparto || "—"}</p>
                            <p className="text-xs text-slate-400 mt-0.5">
                              {new Date(p.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}
                            </p>
                          </div>
                          <span className="text-xs font-semibold text-violet-700 whitespace-nowrap">€{(p.total_euro || 0).toFixed(0)}</span>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Dettaglio (destra 3/5) */}
          <Card className="lg:col-span-3 border-slate-200 shadow-sm">
            <CardContent className="p-6">
              {!selected ? (
                <div className="text-center text-slate-400 text-sm py-12">
                  Seleziona un preventivo dalla lista per vederne il dettaglio
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <h2 className="text-xl font-semibold text-slate-800">{selected.patient_name}</h2>
                      <p className="text-sm text-slate-500 mt-1">
                        {selected.insurance} · {selected.reparto} · Totale <span className="font-semibold">€{(selected.total_euro || 0).toFixed(2)}</span>
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        Creato il {new Date(selected.created_at).toLocaleString("it-IT")} da {selected.created_by_email || "—"}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/piano-cura-clinico?opportunityId=${selected.opportunity_id}&consult=1`)}
                        data-testid="view-piano-btn"
                      >
                        <Eye className="w-4 h-4 mr-1" /> Vedi Piano
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => deleteItem(selected.id)}
                        data-testid={`delete-preventivo-${selected.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" /> Elimina
                      </Button>
                    </div>
                  </div>
                  <div className="border border-slate-200 rounded-lg bg-slate-50 p-4 max-h-[55vh] overflow-y-auto">
                    <pre className="whitespace-pre-wrap text-sm font-sans text-slate-800" data-testid="preventivo-body-view">
                      {selected.body_text || "(vuoto)"}
                    </pre>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
